import React, { useEffect, useReducer, useRef } from 'react';
import { Form, Loader } from 'rsuite';
import _ from 'lodash';
import { v4 } from 'uuid';
import { Carrier, OrderListItem } from '../../api/apiTypes';
import { packagePreview } from '../../api/orders';
import { getCarriers } from '../../api/carrier';
import {
  CrossDockCarrier,
  CrossDockTracking,
  CrossDockTrackingRequest,
} from '../../api/crossdocktracking/crossDockTrackingTypes';
import { showSuccessMessage } from '../../utils/message';
import CrossDockTrackingRow from './CrossDockTrackingRow';
import CrossDockTrackingAction from './CrossDockTrackingAction';
import { utils } from '../../utils/utils';
import { getCrossDockTracking, saveCrossDockTrackings } from '../../api/crossdocktracking/crossDockTracking';

interface Props {
  order?: OrderListItem;
  onCancel: () => void;
}

interface Tracking {
  id: string;
  trackingNumber: string;
  trackingFullNumber: string;
  trackingUrl: string;
  carrier: string;
}

interface State {
  trackings: Tracking[];
  carriers: Carrier[];
  status: 'ready' | 'loading' | 'saving';
}

const defaultState: State = {
  trackings: [],
  carriers: [],
  status: 'ready',
};

function getEmptyTracking(): Tracking {
  return {
    id: v4(),
    trackingNumber: '',
    trackingFullNumber: '',
    trackingUrl: '',
    carrier: '',
  };
}

type LoadAction = { type: 'load' };
type SaveAction = { type: 'save', payload: boolean };
type TrackingsSetAction = { type: 'trackings/set', payload: Tracking[] };
type TrackingsAddAction = { type: 'trackings/add' };
type TrackingsEditAction = { type: 'trackings/edit', payload: Tracking };
type CarriersSetAction = { type: 'carriers/set', payload: Carrier[] };
type Action = LoadAction
| SaveAction
| TrackingsSetAction
| TrackingsAddAction
| TrackingsEditAction
| CarriersSetAction;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'load':
      return { ...state, status: 'loading' };
    case 'save':
      return { ...state, status: action.payload ? 'saving' : 'ready' };
    case 'trackings/set':
      return { ...state, status: 'ready', trackings: action.payload };
    case 'trackings/add':
      return { ...state, trackings: [...state.trackings, getEmptyTracking()] };
    case 'trackings/edit':
      return {
        ...state,
        trackings: state.trackings.map((tracking) => {
          if (tracking.id !== action.payload.id) {
            return tracking;
          }

          return action.payload;
        }),
      };
    case 'carriers/set':
      return { ...state, carriers: action.payload };
    default:
      return state;
  }
}

function generateTrackings(
  crossDockTracking: CrossDockTracking,
  numberOfPackages: number,
): Tracking[] {
  const { firstMilePackages } = crossDockTracking;
  const trackings = firstMilePackages.map(firstMilePackage => ({
    id: v4(),
    trackingNumber: firstMilePackage.parcelNumber ?? '',
    trackingFullNumber: firstMilePackage.parcelFullNumber ?? '',
    trackingUrl: firstMilePackage.parcelTrackingUrl ?? '',
    carrier: firstMilePackage.carrier?.identifier ?? '',
  }));
  const difference = numberOfPackages - trackings.length;

  if (difference <= 0) {
    return trackings;
  }

  const generated = _.times(difference, () => getEmptyTracking());

  return [...trackings, ...generated];
}

function getCrossDockCarrier(tracking: Tracking): CrossDockCarrier | undefined {
  if (tracking.carrier) {
    return { identifier: tracking.carrier };
  }

  return undefined;
}

function getCrossDockTrackingRequest(
  orderId: number,
  trackings: Tracking[],
): CrossDockTrackingRequest {
  const firstMilePackages = trackings.map(tracking => ({
    parcelNumber: utils.defaultToUndefined(tracking.trackingNumber),
    parcelFullNumber: utils.defaultToUndefined(tracking.trackingFullNumber),
    parcelTrackingUrl: utils.defaultToUndefined(tracking.trackingUrl),
    carrier: getCrossDockCarrier(tracking),
  }));

  return {
    trackings: [{
      orderId,
      firstMilePackages,
    }],
  };
}

export default function CrossDockTrackingEdit({ order, onCancel }: Props) {
  const [state, dispatch] = useReducer(reducer, defaultState);
  const formRef = useRef<any>();

  async function fetchData(orderId: number, signal: AbortSignal) {
    dispatch({ type: 'load' });

    const [crossDockTracking, numberOfPackages, carriers] = await Promise.all([
      getCrossDockTracking(orderId, signal),
      packagePreview(orderId, signal).then(response => response.data.numberOfPackages),
      getCarriers(),
    ]);

    const generatedTrackings = generateTrackings(
      crossDockTracking,
      numberOfPackages,
    );

    dispatch({ type: 'trackings/set', payload: generatedTrackings });
    dispatch({ type: 'carriers/set', payload: carriers });
  }

  async function saveData(request: CrossDockTrackingRequest) {
    try {
      dispatch({ type: 'save', payload: true });
      await saveCrossDockTrackings(request);
      showSuccessMessage('Cross dock trackings successfully saved');
    } finally {
      dispatch({ type: 'save', payload: false });
    }
  }

  useEffect(() => {
    const abortController = new AbortController();

    if (order) {
      fetchData(order.id, abortController.signal).then();
    }

    return () => { abortController.abort(); };
  }, [order]);

  if (state.status === 'loading') {
    return <Loader />;
  }

  function handleAddPackage() {
    dispatch({ type: 'trackings/add' });
  }

  function handleTrackingChange(tracking: Tracking) {
    dispatch({ type: 'trackings/edit', payload: tracking });
  }

  function handleSave() {
    if (!order) {
      return;
    }

    if (!formRef.current?.check()) {
      return;
    }

    const trackings = state.trackings
      .filter(tracking => tracking.trackingNumber && tracking.trackingUrl);
    const request = getCrossDockTrackingRequest(order.id, trackings);

    saveData(request).then();
  }

  function handleCancel() {
    onCancel();
  }

  return (
    <Form layout="horizontal" ref={formRef}>
      {state.trackings.map((tracking, index) => (
        <CrossDockTrackingRow
          key={tracking.id}
          index={index}
          tracking={tracking}
          carriers={state.carriers}
          onChange={(changed) => handleTrackingChange(changed)}
        />
      ))}
      <CrossDockTrackingAction
        isSaving={state.status === 'saving'}
        onAddPackage={() => handleAddPackage()}
        onSave={() => handleSave()}
        onCancel={() => handleCancel()}
      />
    </Form>
  );
}

CrossDockTrackingEdit.defaultProps = {
  order: undefined,
};
